import { Link } from "react-router-dom";
import { useSystem, useSites, useActivity, useServers } from "@/lib/hooks";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/StatusBadge";
import { StackBadge } from "@/components/StackBadge";
import {
  UploadCloud, Rocket, HardDrive, Play, Square, AlertOctagon, Globe,
  CheckCircle2, XCircle, Activity, Info, AlertTriangle
} from "lucide-react";

function KPI({ testid, icon: Icon, label, value, sub, accent }) {
  return (
    <Card className="p-5 border-border">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
          <div data-testid={testid} className="mt-2 text-3xl font-semibold tracking-tight">
            {value}
          </div>
          {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
        </div>
        <div
          className="h-10 w-10 rounded-lg grid place-items-center"
          style={{ background: accent || "hsl(var(--surface-2))" }}
        >
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </div>
      </div>
    </Card>
  );
}

export default function OverviewPage() {
  const { data: sys, isLoading: sysLoading } = useSystem();
  const { data: sites } = useSites();
  const { data: activity } = useActivity();
  const { data: servers } = useServers();

  const readyServers = (servers || []).filter(
    (s) => s.reachable && s.docker && s.docker_compose && s.caddy && s.webnet
  );
  const liveViaVps = readyServers.length > 0;

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Overview</h1>
          <p className="text-muted-foreground mt-1">Ship any ZIP. Any stack. Your VPS.</p>
        </div>
        <Link to="/upload">
          <Button data-testid="overview-new-site-button" className="gap-2">
            <UploadCloud className="h-4 w-4" /> New Site
          </Button>
        </Link>
      </header>

      {sys?.base_domain && /\.(local|internal|localhost|test|example|invalid)$/i.test(sys.base_domain) && (
        <div
          data-testid="overview-bad-base-domain-warning"
          className="border rounded-xl p-4"
          style={{ background: "hsl(0 60% 12%)", borderColor: "hsl(0 60% 30%)" }}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-red-200">
                Base domain <code className="font-mono">{sys.base_domain}</code> won't get real HTTPS
              </div>
              <div className="text-sm text-red-200/85 mt-1">
                Caddy treats <code className="font-mono">.local / .internal / .test / .example</code> as
                internal domains and refuses to issue Let's Encrypt certificates for them. Browsers will
                show <b>NET::ERR_CERT_AUTHORITY_INVALID</b>. Change your Base domain to a real public
                domain (e.g., <code className="font-mono">emhost.xyz</code>) in{" "}
                <Link className="underline" to="/settings">Settings</Link>, then click "Regenerate configs"
                on each site and redeploy.
              </div>
            </div>
          </div>
        </div>
      )}

      {sysLoading || !sys ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <KPI testid="kpi-total-sites" icon={Globe} label="Total sites" value={sys.total_sites}
               sub={`${sys.running} running · ${sys.stopped} stopped`}
               accent="hsl(188 86% 20%)" />
          <KPI testid="kpi-running-sites" icon={Play} label="Running" value={sys.running}
               sub={sys.building ? `${sys.building} building` : "stable"}
               accent="hsl(173 80% 22%)" />
          <KPI testid="kpi-stopped-sites" icon={Square} label="Stopped" value={sys.stopped}
               sub={sys.failed ? `${sys.failed} failed` : ""}
               accent="hsl(222 18% 22%)" />
          <KPI testid="kpi-disk-usage" icon={HardDrive} label="Workspace disk"
               value={`${Math.round(sys.disk_used_mb)} MB`}
               sub={`${Math.round(sys.disk_free_mb / 1024)} GB free`}
               accent="hsl(43 96% 20%)" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-5 border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Recent sites</h2>
            <Link to="/sites" className="text-sm text-muted-foreground hover:text-foreground">
              View all →
            </Link>
          </div>
          {sites && sites.length === 0 && (
            <div className="py-10 text-center border border-dashed border-border rounded-xl">
              <div className="mx-auto h-10 w-10 rounded-lg surface-2 grid place-items-center mb-3">
                <Rocket className="h-5 w-5" />
              </div>
              <div className="font-medium">No sites yet</div>
              <div className="text-sm text-muted-foreground mt-1">
                Upload a ZIP and emhost will detect the stack and generate Docker + Caddy configs.
              </div>
              <div className="mt-4">
                <Link to="/upload">
                  <Button data-testid="empty-upload-cta" className="gap-2">
                    <UploadCloud className="h-4 w-4" /> Upload ZIP
                  </Button>
                </Link>
              </div>
            </div>
          )}
          <div className="space-y-2">
            {sites && sites.slice(0, 6).map((s) => (
              <Link
                to={`/sites/${s.id}`}
                key={s.id}
                className="flex items-center justify-between gap-3 px-3 py-3 rounded-lg border border-border hover:bg-[hsl(var(--surface-2))]/40 transition-colors"
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{s.name}</div>
                  <div className="text-xs text-muted-foreground font-mono truncate">
                    {s.subdomain}.{sys?.base_domain || "emhost.local"}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StackBadge type={s.stack_type} />
                  <StatusBadge status={s.status} />
                </div>
              </Link>
            ))}
          </div>
        </Card>

        <Card className="p-5 border-border">
          <h2 className="text-lg font-semibold mb-4">System</h2>
          <div className="space-y-3 text-sm">
            <Row
              k="Deploy mode"
              v={liveViaVps
                ? `Live via ${readyServers.length} VPS${readyServers.length === 1 ? "" : "es"}`
                : "Simulation only"}
            />
            <Row k="Ready servers" v={`${readyServers.length} / ${(servers || []).length}`} />
            <Row k="Base domain" v={sys?.base_domain} mono />
            <Row k="Workspace (panel host)" v={sys?.workspace_path} mono />
            <Row k="Local Docker (panel host)" v={sys?.docker_available ? "available" : "unavailable — expected"} />
            <Row k="Failed" v={sys?.failed ?? 0} />
            <Row k="Building" v={sys?.building ?? 0} />
          </div>
          <div className="mt-4 text-[11px] text-muted-foreground leading-relaxed">
            {liveViaVps ? (
              <>Real deploys go over SSH to your registered VPS. The panel host itself doesn't need Docker.</>
            ) : (
              <>
                <AlertOctagon className="h-3 w-3 inline mr-1" />
                Add a VPS on <Link className="underline" to="/servers">Servers</Link> and bootstrap it to enable real deploys. "Local Docker" refers to the panel host and is expected to be unavailable here.
              </>
            )}
          </div>
        </Card>
      </div>

      <Card className="p-5 border-border" data-testid="activity-feed-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Activity className="h-4 w-4" /> Activity
          </h2>
          <div className="text-xs text-muted-foreground">latest 30 events</div>
        </div>
        {(!activity || activity.length === 0) ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            No activity yet — upload a ZIP or add a server to see events here.
          </div>
        ) : (
          <ol className="space-y-2">
            {activity.map((e) => (
              <ActivityRow key={e.id} e={e} />
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}

function Row({ k, v, mono }) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-border last:border-b-0 pb-2 last:pb-0">
      <span className="text-muted-foreground">{k}</span>
      <span className={mono ? "font-mono text-xs truncate max-w-[60%]" : ""}>{v ?? "—"}</span>
    </div>
  );
}

function ActivityRow({ e }) {
  const map = {
    success: { Icon: CheckCircle2, color: "text-emerald-400" },
    error:   { Icon: XCircle,      color: "text-red-400" },
    warn:    { Icon: AlertTriangle,color: "text-amber-400" },
    info:    { Icon: Info,         color: "text-sky-400" },
  };
  const s = map[e.status] || map.info;
  const Icon = s.Icon;
  const ts = e.ts ? new Date(e.ts) : null;
  const linkable = e.site_id ? `/sites/${e.site_id}` : (e.server_id ? "/servers" : null);
  const Inner = (
    <div className="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-border hover:bg-[hsl(var(--surface-2))]/40 transition-colors">
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${s.color}`} />
      <div className="min-w-0 flex-1">
        <div className="text-sm truncate">{e.title}</div>
        {e.message && <div className="text-[12px] text-muted-foreground truncate font-mono">{e.message}</div>}
      </div>
      <div className="text-[11px] text-muted-foreground shrink-0 font-mono">
        {ts ? ts.toLocaleTimeString() : ""}
        {e.duration_ms != null && <span className="ml-2">{Math.round(e.duration_ms)}ms</span>}
      </div>
    </div>
  );
  return linkable ? <Link to={linkable}>{Inner}</Link> : Inner;
}
